import { FC, Suspense } from 'react';
import { FaBoxOpen, FaFile, FaFolder } from 'react-icons/fa';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button/Button';
import { SectionIconLink } from '../../components/links/SectionIconLink';
import { DocumentListSection } from '../../components/oneDrive/DocumentList';
import { NavPage } from '../../components/page/Breadcrumbs';
import { ContentSection } from '../../components/page/ContentSection';
import { PageContainer } from '../../components/page/PageContainer';
import { PageHeader, PageHeaderSize } from '../../components/page/PageHeader';
import { PageSizer } from '../../components/page/PageSizer';
import { SectionHeader } from '../../components/page/SectionHeader';
import { useSectionDetails, useSections } from '../../services/graph/hooks/category';
import { useFilesInFolder } from '../../services/graph/hooks/documentHelper';
import { useIntroduction } from '../../services/graph/hooks/introduction';
import { Loading } from '../site/Loading';
import strings from './documents.translations';

export const MyDocumentsSectionPage: FC = () => {
  const { sectionId, subSectionId } = useParams<'sectionId' | 'subSectionId'>();

  if (!sectionId) throw new Error('No section id provided');

  const effectiveSectionId = subSectionId || sectionId;
  const { sectionData: currentSectionData } = useSectionDetails(effectiveSectionId);
  if (!currentSectionData) throw new Error('No section data provided');

  const { sectionData: parentSectionData } = useSectionDetails(sectionId);
  const navigate = useNavigate();

  // Recursive function to build breadcrumbs
  const buildBreadcrumbs = (sectionId: string, subSectionId?: string): NavPage[] => {
    if (!subSectionId) {
      return [
        {
          name: strings.common.pages.documents,
          to: '/documents'
        },
        {
          name: strings.myDocuments,
          to: '/documents/myDocuments'
        },
        {
          name: parentSectionData.name,
          to: `/documents/myDocuments/sections/${sectionId}`
        }
      ];
    } else {
      const parentBreadcrumbs = buildBreadcrumbs(sectionId);
      return [
        ...parentBreadcrumbs,
        {
          name: currentSectionData.name,
          to: `/documents/myDocuments/sections/${sectionId}/subsections/${subSectionId}`
        }
      ];
    }
  };

  const breadcrumbs = buildBreadcrumbs(sectionId, subSectionId);

  const introduction = useIntroduction();
  const subSections = useSections(effectiveSectionId);

  return (
    <PageContainer>
      <PageHeader
        title={strings.myDocuments}
        subtitle={currentSectionData.name}
        description={currentSectionData.description}
        imageUrl={currentSectionData.coverImage || introduction.data?.backgroundImages.documentsPage}
        breadcrumbs={breadcrumbs}
        size={PageHeaderSize.LARGE}
      />
      <Suspense fallback={<Loading fullPage={false} />}>
        {currentSectionData.longDescription && (
          <ContentSection>
            <p>{currentSectionData.longDescription}</p>
          </ContentSection>
        )}
        <PageSizer className="py-5 flex flex-col gap-10">
          {subSections.length > 0 && (
            <div>
              <SectionHeader title={strings.subSections} Icon={FaFolder} className="mb-5"></SectionHeader>
              <div className="grid gap-x-2 gap-y-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {subSections.map(subSection => {
                  if (!subSection.id) throw new Error('No sub section id');
                  return (
                    <SectionIconLink
                      driveItemId={subSection.id}
                      parentSectionId={effectiveSectionId}
                      key={subSection.id}
                    ></SectionIconLink>
                  );
                })}
              </div>
            </div>
          )}
          <DocumentListSection
            title={currentSectionData.name}
            Icon={FaFile}
            filterable
            updateable
            deleteable
            getDocuments={() => useFilesInFolder(effectiveSectionId)}
            customComponent={
              <Button Icon={FaBoxOpen} primary onClick={() => navigate(`/documents/upload/${effectiveSectionId}`)}>
                {strings.uploadFile}
              </Button>
            }
          />
        </PageSizer>
      </Suspense>
    </PageContainer>
  );
};
